"use client";

import { useEffect, useState, type ReactElement } from "react";
import Link from "next/link";
import { Logo } from "@/components/logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { APP_VERSION } from "@/lib/version";

function Icon({ path, className = "h-5 w-5" }: { path: string; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className={className} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

// On-brand inline UI mockups — recreations of the real screens using the design tokens (no
// screenshots, so they're CSP-safe, weightless and never go stale). Attached to key sections.
function SendMockup() {
  return (
    <div aria-hidden className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-card">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">Compose</span>
        <span className="rounded-full bg-accent-50 px-2 py-0.5 text-[11px] font-medium text-accent-700 ring-1 ring-inset ring-accent-200 dark:bg-accent-500/15 dark:text-accent-300 dark:ring-accent-500/30">GSM-7</span>
      </div>
      <div className="mt-3 space-y-2 text-xs">
        <div className="rounded-lg border border-gray-200 px-3 py-2 text-gray-600"><span className="text-gray-400">From</span> · BrandA</div>
        <div className="rounded-lg border border-gray-200 px-3 py-2 text-gray-600"><span className="text-gray-400">To</span> · 252 61 234 5678</div>
      </div>
      <div className="mt-2 rounded-2xl bg-gray-100 p-3 dark:bg-gray-500/10">
        <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white px-3 py-2 text-[13px] text-gray-800 shadow-sm ring-1 ring-black/5">Your code is 4821. It expires in 10 minutes.</div>
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 text-xs">
        <span className="text-gray-500">Est. credits <span className="font-semibold text-brand-600 dark:text-brand-400">1</span></span>
        <span className="rounded-lg bg-brand-600 px-3 py-1.5 text-[11px] font-semibold text-pure-white">Review &amp; Send</span>
      </div>
    </div>
  );
}

function BulkMockup() {
  return (
    <div aria-hidden className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-card">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">June promo</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-success-50 px-2 py-0.5 text-[11px] font-medium text-success-700 ring-1 ring-inset ring-success-200 dark:bg-success-500/15 dark:text-success-300 dark:ring-success-500/30"><span className="h-1.5 w-1.5 rounded-full bg-success-500" />Sending</span>
      </div>
      <p className="mt-2 text-xs text-gray-500">2,000 recipients · 1 batch · GSM-7</p>
      <div className="mt-3">
        <div className="mb-1 flex justify-between text-[11px] text-gray-500"><span>Enqueuing to send queue</span><span className="tabular-nums">1,240 / 2,000</span></div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-500/15"><div className="h-full rounded-full bg-gradient-to-r from-brand-500 to-accent-500" style={{ width: "62%" }} /></div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        {[["Local", "1,480"], ["National", "500"], ["Intl", "20"]].map(([k, v]) => (
          <div key={k} className="rounded-xl bg-gray-50 py-2 dark:bg-gray-500/10"><p className="text-sm font-bold text-gray-900">{v}</p><p className="text-[10px] text-gray-500">{k}</p></div>
        ))}
      </div>
    </div>
  );
}

function SupportMockup() {
  return (
    <div aria-hidden className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-card">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">#128 · Sender ID request</span>
        <span className="rounded-full bg-accent-50 px-2 py-0.5 text-[11px] font-medium text-accent-700 ring-1 ring-inset ring-accent-200 dark:bg-accent-500/15 dark:text-accent-300 dark:ring-accent-500/30">Support replied</span>
      </div>
      <div className="mt-3 space-y-2">
        <div className="flex justify-end"><div className="max-w-[80%] rounded-2xl rounded-br-sm bg-brand-600 px-3 py-2 text-[13px] text-pure-white">How long does sender ID approval take?</div></div>
        <div className="flex justify-start"><div className="max-w-[80%] rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2 text-[13px] text-gray-800 dark:bg-gray-500/15 dark:text-gray-200">Usually within one business day — we&apos;ll notify you right here.</div></div>
      </div>
      <div className="mt-3 rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-400">Write a reply…</div>
    </div>
  );
}

function PersonalizedMockup() {
  return (
    <div aria-hidden className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-card">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">Personalized</span>
        <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 ring-1 ring-inset ring-brand-100 dark:bg-brand-500/15 dark:text-brand-300 dark:ring-brand-500/30">statements.csv</span>
      </div>
      <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 text-[11px] dark:border-gray-500/20">
        <div className="grid grid-cols-3 bg-gray-50 font-medium text-gray-500 dark:bg-gray-500/10">
          <span className="px-2 py-1">phone</span><span className="px-2 py-1">name</span><span className="px-2 py-1">balance</span>
        </div>
        <div className="grid grid-cols-3 border-t border-gray-100 text-gray-700 dark:border-gray-500/20 dark:text-gray-300">
          <span className="px-2 py-1 tabular-nums">2526…78</span><span className="px-2 py-1">Amina</span><span className="px-2 py-1 tabular-nums">250</span>
        </div>
      </div>
      <div className="mt-3 rounded-2xl bg-gray-100 p-3 dark:bg-gray-500/10">
        <div className="max-w-[92%] rounded-2xl rounded-tl-sm bg-white px-3 py-2 text-[13px] text-gray-800 shadow-sm ring-1 ring-black/5">
          Hi <span className="rounded bg-brand-50 px-1 font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">Amina</span>, your balance is{" "}
          <span className="rounded bg-brand-50 px-1 font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">250</span> credits.
        </div>
      </div>
    </div>
  );
}

function ContactsMockup() {
  return (
    <div aria-hidden className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-card">
      <span className="text-sm font-semibold text-gray-900">Groups</span>
      <div className="mt-3 space-y-2">
        {[["VIP customers", "1,240"], ["Newsletter", "8,900"]].map(([n, c]) => (
          <div key={n} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-500/20">
            <div><p className="text-sm font-medium text-gray-900">{n}</p><p className="text-[11px] text-gray-500">{c} contacts</p></div>
            <span className="rounded-lg bg-brand-600 px-2.5 py-1 text-[11px] font-semibold text-pure-white">Send</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2 text-[11px] text-gray-500">
        <span className="rounded-full bg-accent-50 px-2 py-0.5 font-medium text-accent-700 ring-1 ring-inset ring-accent-200 dark:bg-accent-500/15 dark:text-accent-300 dark:ring-accent-500/30">Segment</span>
        balance &gt; 100 · re-evaluated on send
      </div>
    </div>
  );
}

function ReportsMockup() {
  const rows: [string, string, string, string][] = [
    ["Delivered", "98,420", "bg-success-500", "98%"],
    ["Sent", "1,180", "bg-accent-500", "12%"],
    ["Failed", "400", "bg-danger-500", "5%"]
  ];
  return (
    <div aria-hidden className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-card">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">Delivery reports</span>
        <span className="rounded-full bg-success-50 px-2 py-0.5 text-[11px] font-medium text-success-700 ring-1 ring-inset ring-success-200 dark:bg-success-500/15 dark:text-success-300 dark:ring-success-500/30">98.7% delivered</span>
      </div>
      <div className="mt-3 space-y-2">
        {rows.map(([label, count, color, w]) => (
          <div key={label} className="flex items-center gap-2 text-xs">
            <span className="w-16 shrink-0 text-gray-500">{label}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-500/15">
              <div className={`h-full rounded-full ${color}`} style={{ width: w }} />
            </div>
            <span className="w-12 shrink-0 text-right font-medium tabular-nums text-gray-700 dark:text-gray-300">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BillingMockup() {
  return (
    <div aria-hidden className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-card">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">SMS credit</p>
      <p className="mt-1 text-3xl font-bold tracking-tight text-gray-900">
        128,400 <span className="text-sm font-medium text-gray-400">credits</span>
      </p>
      <div className="mt-4 rounded-xl border border-gray-200 p-3 dark:border-gray-500/20">
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium text-gray-900">June bundle</span>
          <span className="text-gray-500">62% left</span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-500/15">
          <div className="h-full rounded-full bg-brand-500" style={{ width: "62%" }} />
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <span className="rounded-lg bg-brand-600 px-3 py-1.5 text-[11px] font-semibold text-pure-white">Buy with EVC</span>
      </div>
    </div>
  );
}

function GettingStartedMockup() {
  const items: [string, boolean][] = [
    ["Account approved", true],
    ["Sender ID “BrandA” approved", true],
    ["Credit added", true],
    ["Send your first SMS", false]
  ];
  return (
    <div aria-hidden className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-card">
      <span className="text-sm font-semibold text-gray-900">Setup checklist</span>
      <div className="mt-3 space-y-2.5">
        {items.map(([label, done]) => (
          <div key={label} className="flex items-center gap-2.5 text-sm">
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${done ? "bg-success-500 text-pure-white" : "border border-gray-300 text-transparent dark:border-gray-500/40"}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} className="h-3 w-3"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
            </span>
            <span className={done ? "text-gray-500 line-through decoration-gray-300" : "font-medium text-gray-900"}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplatesMockup() {
  return (
    <div aria-hidden className="rounded-2xl border border-gray-200/70 bg-white p-4 shadow-card">
      <span className="text-sm font-semibold text-gray-900">Templates</span>
      <div className="mt-3 space-y-2">
        {[["OTP code", "Your code is {{code}}. It expires in 10 minutes."], ["Low balance", "Hi {{name}}, your balance is {{balance}}. Top up to keep sending."]].map(([name, body]) => (
          <div key={name} className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-500/20">
            <p className="text-xs font-semibold text-gray-900">{name}</p>
            <p className="mt-0.5 truncate text-[11px] text-gray-500">{body}</p>
          </div>
        ))}
      </div>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {["{{code}}", "{{name}}", "{{balance}}"].map((v) => (
          <span key={v} className="rounded-md border border-brand-200 bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/15 dark:text-brand-300">{v}</span>
        ))}
      </div>
    </div>
  );
}

const MOCKUPS: Partial<Record<string, () => ReactElement>> = {
  "getting-started": GettingStartedMockup,
  "send-sms": SendMockup,
  bulk: BulkMockup,
  personalized: PersonalizedMockup,
  contacts: ContactsMockup,
  templates: TemplatesMockup,
  reports: ReportsMockup,
  billing: BillingMockup,
  support: SupportMockup
};

// Each section deep-links to the matching portal screen. Signed-out visitors get bounced to /login.
const HREFS: Record<string, string> = {
  "getting-started": "/account",
  "send-sms": "/sms/send",
  bulk: "/sms/bulk",
  personalized: "/sms/personalized",
  contacts: "/contacts",
  templates: "/templates",
  reports: "/sms/reports",
  billing: "/billing",
  support: "/support"
};

const TROUBLESHOOTING = [
  { q: "My message shows “Failed” — why?", a: "Open Delivery Reports and check the failure reason. Common causes are an invalid or unreachable number, or the recipient having opted out of that sender ID." },
  { q: "I get “Recipient has opted out” when sending", a: "That recipient replied STOP to the sender you're using. Opt-outs are per sender ID — try another approved sender, or review the Opt-outs page." },
  { q: "My sender ID is rejected", a: "You can only send from an approved sender ID. Request one on My Account; sending with an unapproved “from” name is blocked." },
  { q: "I can't send — no credit", a: "Buy a package or top up with your EVC / mobile wallet on Billing. Credit is added the moment the payment is approved." },
  { q: "I lost my API key", a: "For security, a key is shown only once when created. If you lost it, revoke it on API Keys and generate a new one." }
];

interface Section {
  id: string;
  title: string;
  path: string;
  intro: string;
  steps: string[];
}

const SECTIONS: Section[] = [
  {
    id: "getting-started",
    title: "Getting started",
    path: "M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.63 8.37m5.96 6a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z",
    intro: "Set up your account so you can start sending.",
    steps: [
      "Request an account from the sign-in page — an admin reviews it and shares your login details once approved.",
      "Sign in, then open My Account and request a sender ID (the “from” name your recipients will see). It must be approved before you can send with it.",
      "Add SMS credit from Billing — buy a package or top up with your EVC / mobile wallet. Credit is added the moment the payment is approved."
    ]
  },
  {
    id: "send-sms",
    title: "Send a single SMS",
    path: "M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5",
    intro: "Send one message to one recipient.",
    steps: [
      "Go to Send SMS, choose your approved sender ID and enter the destination number.",
      "Type your message — the live preview shows the encoding, segment count and estimated credits.",
      "Optionally turn on Flash or “wait for delivery”, then click Review & Send and confirm.",
      "Track the outcome under Delivery Reports, or look up a specific message under Message Lookup."
    ]
  },
  {
    id: "bulk",
    title: "Bulk campaigns",
    path: "M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m0 0a23.91 23.91 0 001.014-5.395",
    intro: "Send the same message to many recipients at once.",
    steps: [
      "Go to Bulk SMS. Paste numbers, upload a .txt/.csv file, or pick a saved contact group.",
      "Write your message and check the campaign summary — recipients, segments and estimated credits by category.",
      "Send now, or schedule it for later. The gateway keeps sending in the background even if you close the page.",
      "Follow progress under Campaigns, and cancel a scheduled send anytime before it fires under Scheduled."
    ]
  },
  {
    id: "personalized",
    title: "Personalized SMS",
    path: "M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z",
    intro: "Merge each recipient’s own data into the message.",
    steps: [
      "Go to Personalized SMS and upload a CSV (or load a contact group). The first row is the column headers.",
      "Insert columns as {{placeholders}} in your template — e.g. “Hi {{name}}, your balance is {{balance}}.”",
      "Preview the first recipient’s merged message and review the credit estimate, then send or schedule."
    ]
  },
  {
    id: "contacts",
    title: "Contacts, groups & segments",
    path: "M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z",
    intro: "Save your audience once and reuse it.",
    steps: [
      "In Contacts, add recipients one by one or import a whole list from CSV (extra columns become merge variables).",
      "File contacts into groups (e.g. “VIP customers”) and send to a whole group in one click.",
      "Build dynamic segments — a saved rule that re-selects matching contacts every time you send."
    ]
  },
  {
    id: "templates",
    title: "Message templates",
    path: "M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z",
    intro: "Save messages you send often.",
    steps: [
      "In Templates, create reusable messages (including {{placeholders}} for personalization).",
      "Pick a template from the composer on any Send, Bulk or Personalized screen to fill the message instantly."
    ]
  },
  {
    id: "reports",
    title: "Delivery reports & inbox",
    path: "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z",
    intro: "See what was delivered and read replies.",
    steps: [
      "Delivery Reports shows every message from queued to delivered, with failure-reason insights.",
      "Usage summarizes volumes and spend over time.",
      "Inbox shows replies handsets send to your sender IDs — read them and reply in a click."
    ]
  },
  {
    id: "billing",
    title: "Billing & packages",
    path: "M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0c1.1.128 1.907 1.077 1.907 2.185z",
    intro: "Manage credit, packages and invoices.",
    steps: [
      "In Billing, see your available credit and active packages.",
      "Buy or renew a package with your EVC / mobile wallet — you’ll get a prompt on your phone to approve.",
      "Review your credit history, payment history and invoices anytime."
    ]
  },
  {
    id: "support",
    title: "Get help",
    path: "M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z",
    intro: "We’re here when you need us.",
    steps: [
      "Open Support and create a ticket describing your issue — pick a category and priority.",
      "Our team replies right inside the ticket; you’ll get a notification when they do.",
      "Reply to keep the conversation going, and close the ticket once it’s resolved."
    ]
  }
];

function ctaPrimary() {
  return "inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-pure-white shadow-sm transition hover:bg-brand-700 active:scale-[0.98]";
}

export default function GuidePage() {
  // Scroll-spy: highlight the table-of-contents entry for the section currently in view.
  const [active, setActive] = useState<string>(SECTIONS[0].id);
  useEffect(() => {
    const els = SECTIONS
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -55% 0px", threshold: 0 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Reading-progress bar + back-to-top button.
  const [progress, setProgress] = useState(0);
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      setProgress(max > 0 ? Math.min(100, (el.scrollTop / max) * 100) : 0);
      setShowTop(el.scrollTop > 600);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Reading progress */}
      <div aria-hidden className="fixed inset-x-0 top-16 z-40 h-0.5">
        <div className="h-full bg-gradient-to-r from-brand-500 to-accent-500 transition-[width] duration-150" style={{ width: `${progress}%` }} />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-gray-100 bg-white/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <Link href="/" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-50 p-1.5 ring-1 ring-brand-100">
              <Logo className="h-full w-full" />
            </span>
            <span className="text-base font-bold tracking-tight">Hormuud SMS</span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2">
            <Link href="/#features" className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:text-gray-900 md:block">
              Features
            </Link>
            <Link href="/#pricing" className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:text-gray-900 md:block">
              Pricing
            </Link>
            <Link href="/#faq" className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:text-gray-900 md:block">
              FAQ
            </Link>
            <Link href="/guide" aria-current="page" className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-brand-700 dark:text-brand-300 md:block">
              Guide
            </Link>
            <ThemeToggle />
            <Link href="/login" className={ctaPrimary() + " !px-4 !py-2"}>
              Sign in
            </Link>
          </div>
        </div>
      </header>

      {/* Title */}
      <section className="border-b border-gray-100">
        <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
          <p className="text-sm font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400">Customer guide</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">How to use the Customer Portal</h1>
          <p className="mt-3 max-w-2xl text-gray-600">
            A quick, step-by-step guide to sending SMS, managing your audience, tracking delivery and handling
            billing — everything you can do from the portal.
          </p>
        </div>
      </section>

      {/* Body: sticky TOC + sections */}
      <div className="mx-auto max-w-6xl px-5 py-12">
        <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-12">
          {/* Table of contents */}
          <nav aria-label="Contents" className="mb-10 lg:mb-0">
            <div className="lg:sticky lg:top-24">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">On this page</p>
              <ul className="space-y-1.5">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      aria-current={active === s.id ? "location" : undefined}
                      className={`block rounded-md border-l-2 px-2 py-1 text-sm transition ${
                        active === s.id
                          ? "border-brand-500 bg-brand-50 font-semibold text-brand-700 dark:bg-brand-500/10 dark:text-brand-300"
                          : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-brand-700 dark:hover:text-brand-300"
                      }`}
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          {/* Sections */}
          <div className="space-y-12">
            {SECTIONS.map((s, i) => {
              const Mockup = MOCKUPS[s.id];
              return (
                <section key={s.id} id={s.id} className="scroll-mt-24">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100 dark:bg-brand-500/10 dark:text-brand-300 dark:ring-brand-500/20">
                        <Icon path={s.path} />
                      </span>
                      <div>
                        <p className="text-xs font-medium text-gray-400">Step {i + 1}</p>
                        <h2 className="text-xl font-bold tracking-tight text-gray-900">{s.title}</h2>
                      </div>
                    </div>
                    {HREFS[s.id] && (
                      <Link
                        href={HREFS[s.id]}
                        className="hidden shrink-0 items-center gap-1 text-sm font-medium text-brand-600 transition hover:gap-1.5 hover:text-brand-700 dark:text-brand-400 sm:inline-flex"
                      >
                        Open in portal
                        <Icon path="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" className="h-4 w-4" />
                      </Link>
                    )}
                  </div>
                  <p className="mt-3 text-gray-600">{s.intro}</p>
                  <div className={Mockup ? "mt-4 gap-8 lg:grid lg:grid-cols-2 lg:items-start" : "mt-4"}>
                    <ol className="space-y-2.5">
                      {s.steps.map((step, j) => (
                        <li key={j} className="flex gap-3 text-sm leading-relaxed text-gray-700">
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[11px] font-bold text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
                            {j + 1}
                          </span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                    {Mockup && (
                      <div className="mt-6 lg:mt-0">
                        <Mockup />
                      </div>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </div>

      {/* Troubleshooting */}
      <section id="troubleshooting" className="scroll-mt-24 border-t border-gray-100 py-16">
        <div className="mx-auto max-w-3xl px-5">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Troubleshooting</h2>
          <p className="mt-2 text-gray-600">Quick answers to the most common issues.</p>
          <div className="mt-8 space-y-3">
            {TROUBLESHOOTING.map((t) => (
              <details
                key={t.q}
                className="group rounded-2xl border border-gray-200/70 bg-white px-5 shadow-card [&_summary]:list-none"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 py-4 text-sm font-semibold text-gray-900">
                  {t.q}
                  <Icon path="M19.5 8.25l-7.5 7.5-7.5-7.5" className="h-4 w-4 shrink-0 text-gray-400 transition group-open:rotate-180" />
                </summary>
                <p className="pb-4 text-sm leading-relaxed text-gray-600">{t.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 pb-16">
        <div className="mx-auto max-w-6xl rounded-3xl bg-brand-700 px-6 py-12 text-center text-pure-white sm:px-12">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Ready to send your first message?</h2>
          <p className="mx-auto mt-3 max-w-lg text-pure-white/80">
            Sign in to your account, or request access and your Hormuud account manager will get you set up.
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link href="/login" className="inline-flex items-center justify-center gap-2 rounded-lg bg-pure-white px-5 py-2.5 text-sm font-semibold text-brand-700 shadow-sm transition hover:bg-brand-50 active:scale-[0.98]">
              Sign in
            </Link>
            <Link href="/login" className="inline-flex items-center justify-center gap-2 rounded-lg border border-pure-white/40 px-5 py-2.5 text-sm font-semibold text-pure-white transition hover:bg-pure-white/10 active:scale-[0.98]">
              Request an account
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 sm:flex-row">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Logo className="h-5 w-5" />
            <span>Hormuud SMS · Customer Portal</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <Link href="/" className="transition hover:text-gray-600">Home</Link>
            <Link href="/login" className="transition hover:text-gray-600">Sign in</Link>
            <span>v{APP_VERSION}</span>
          </div>
        </div>
      </footer>

      {/* Back to top */}
      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to top"
          className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-brand-600 text-pure-white shadow-lg transition hover:bg-brand-700 active:scale-95"
        >
          <Icon path="M4.5 15.75l7.5-7.5 7.5 7.5" className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
